import {
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import GKModal from "components/GKModal";
import GKSelect from "components/GKSelect";
import GKTextField from "components/GKTextField";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { MdDelete } from "react-icons/md";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { theme } from "utils/Theme";

const BrokerMasterDetailsModal = ({
  isModalOpen,
  setIsModalOpen,
  refetch,
  detailsData,
}) => {
  const { id } = useParams();

  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      fileType: detailsData?.id ? detailsData.fileType : "",
      dailyBulk: detailsData?.id ? detailsData.dailyBulk : "",
      template: [],
      version: detailsData?.id ? detailsData.version : "",
    },
  });

  const { mutate: handleCreateBrokerDetails } = useMutation({
    mutationKey: ["CREATE_BROKER_DETAILS"],
    mutationFn: (data) =>
      axiosInstance.post("/broker-integration/create/", data),
    onSuccess: () => {
      refetch();
      setIsModalOpen(false);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const { mutate: handleUpdateBrokerDetails } = useMutation({
    mutationKey: ["UPDATE_BROKER_DETAILS"],
    mutationFn: (data) =>
      axiosInstance.patch(
        `/broker-integration/${detailsData.id}/update/`,
        data
      ),
    onSuccess: () => {
      refetch();
      setIsModalOpen(false);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  useEffect(() => {
    if (detailsData?.template) {
      setValue("template", [{ name: detailsData.template }]);
    }
  }, [detailsData, setValue]);

  const onSubmit = (values) => {
    const brokerData = new FormData();
    brokerData.append("file_type", values.fileType);
    brokerData.append("daily_bulk", values.dailyBulk);
    brokerData.append("version", values.version);
    if (values.template.length > 0) {
      brokerData.append("template", values.template[0]);
    }
    brokerData.append("broker_id", id);

    if (detailsData?.id) {
      handleUpdateBrokerDetails(brokerData);
    } else {
      handleCreateBrokerDetails(brokerData);
    }
  };

  return (
    <GKModal
      open={isModalOpen}
      setOpen={setIsModalOpen}
      modalTitle={
        detailsData?.id ? "Update Broker Details" : "Add New Broker Details"
      }
      footer={
        <Grid container justifyContent={"flex-end"} gap={1}>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
            variant="outlined"
            onClick={() => setIsModalOpen(false)}
          >
            Close
          </Button>
          <Button
            sx={{
              fontSize: "0.7rem",
              fontFamily: '"Arial", sans-serif',
              textTransform: "none",
            }}
            type="submit"
            form="create-broker-details-form"
            variant="outlined"
          >
            {detailsData?.id ? "Update" : "Submit"}
          </Button>
        </Grid>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} id="create-broker-details-form">
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Controller
              name="fileType"
              control={control}
              render={({ field }) => (
                <FormControl variant="standard" fullWidth>
                  <GKSelect {...field} inputLabel="FileType">
                    <MenuItem value={"csv"}>CSV</MenuItem>
                    <MenuItem value={"pdf"}>PDF</MenuItem>
                  </GKSelect>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="dailyBulk"
              control={control}
              render={({ field }) => (
                <FormControl variant="standard" fullWidth>
                  <GKSelect {...field} inputLabel="DailyBulk">
                    <MenuItem value={"daily"}>Daily</MenuItem>
                    <MenuItem value={"bulk"}>Bulk</MenuItem>
                  </GKSelect>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <Controller
                name="version"
                control={control}
                render={({ field }) => (
                  <GKTextField inputLabel="Version" {...field} />
                )}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="template"
              control={control}
              render={({ field }) => (
                <>
                  {field.value.length > 0 ? (
                    <Grid item xs={12}>
                      {Array.from(field.value).map((file, index) => (
                        <Grid
                          container
                          border={"solid 1px "}
                          borderColor={theme.palette.border.main}
                          borderRadius={2}
                          p={1}
                          my={1}
                          key={file.name}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            fontWeight={600}
                            fontSize={12}
                            width={"30%"}
                            textOverflow={"ellipsis"}
                            overflow={"hidden"}
                          >
                            {file.name || file.mediaFileName}
                          </Typography>
                          <IconButton
                            color="error"
                            onClick={() => {
                              const newFiles = Array.from(field.value);
                              newFiles.splice(index, 1);
                              setValue("media_file", newFiles);
                              field.onChange(newFiles);
                            }}
                          >
                            <MdDelete />
                          </IconButton>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Button
                      sx={{
                        fontSize: "0.7rem",
                        fontFamily: '"Arial", sans-serif',
                        textTransform: "none",
                      }}
                      component="label"
                      variant="contained"
                    >
                      Upload File
                      <input
                        type="file"
                        hidden
                        onChange={(e) => {
                          setValue("media_file", e.target.files);
                          field.onChange(e.target.files);
                        }}
                      />
                    </Button>
                  )}
                </>
              )}
            />
          </Grid>
        </Grid>
      </form>
    </GKModal>
  );
};

export default BrokerMasterDetailsModal;
